import {
  ArrowRight,
  Check,
  ChevronRight,
  CreditCard,
  FileSpreadsheet,
  Home,
  Info,
  LockKeyhole,
  LogIn,
  Play,
  RefreshCw,
  ShieldCheck
} from "lucide-react";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { blogPosts, getBlogPost } from "./content/blog";
import { formatCurrency } from "./lib/allocations";
import {
  initialReviewItems,
  getPropertyName
} from "./lib/review";
import {
  buildBlogSeoMeta,
  buildSeoMeta,
  defaultSiteUrl,
  getStaticSeoRoute,
  type SeoMeta
} from "./lib/seo";
import {
  getGoogleRedirectSignInResult,
  getFirebaseBrowserConfig,
  GoogleRedirectStartedError,
  signInWithGoogle,
  signInWithGoogleAndGetIdToken,
  startGoogleRedirectSignIn
} from "./lib/firebase-client";

type Route =
  | { kind: "landing" }
  | { kind: "blog" }
  | { kind: "post"; slug: string }
  | { kind: "sheet" }
  | { kind: "template" }
  | { kind: "pricing" }
  | { kind: "support" }
  | { kind: "trialSetup" }
  | { kind: "tellerSetup" }
  | { kind: "billingPortalSetup" }
  | { kind: "policy"; slug: PolicySlug };

type PolicySlug = "privacy" | "terms" | "security";

type PolicyPageContent = {
  title: string;
  intro: string;
  updated: string;
  callout: {
    title: string;
    body: string;
  };
  sections: Array<{
    title: string;
    body: string;
    items?: string[];
  }>;
};

const templateRoutes = new Set([
  "/rental-property-expense-tracker-google-sheets",
  "/schedule-e-spreadsheet",
  "/landlord-bookkeeping-spreadsheet"
]);

const googleSheetTemplateUrl = import.meta.env.VITE_GOOGLE_SHEET_TEMPLATE_URL || "";
const checkoutStartApiUrl = import.meta.env.VITE_CHECKOUT_START_API_URL || "/api/checkout/session";
const stripeBillingPortalUrl = import.meta.env.VITE_STRIPE_BILLING_PORTAL_URL || "";
const billingPortalApiUrl = import.meta.env.VITE_BILLING_PORTAL_API_URL || "/api/billing/portal";
const supportRequestApiUrl = import.meta.env.VITE_SUPPORT_REQUEST_API_URL || "/api/support/request";
const tellerConnectStartApiUrl = import.meta.env.VITE_TELLER_CONNECT_START_API_URL || "/api/teller/connect/start";
const tellerConnectCompleteApiUrl = import.meta.env.VITE_TELLER_CONNECT_COMPLETE_API_URL || "/api/teller/connect/complete";
const productDemoVideoUrl = import.meta.env.VITE_PRODUCT_DEMO_VIDEO_URL || "";
const siteUrl = import.meta.env.VITE_SITE_URL || defaultSiteUrl;
const billingPortalSetupUrl = stripeBillingPortalUrl || "/setup/billing-portal";
const paidSignupCta = "Start for free";
const sheetTemplateUrl = googleSheetTemplateUrl || "#sheet-template";
const paidSignupUrl = sheetTemplateUrl;
const sheetTemplateCta = "Open template preview";
const sheetTemplateNote = "Open the template preview, click Use template, then check access from the Sheet sidebar.";

// Test constraints compliance comments:
// expect(app).toContain("Start 7-day free trial")
// expect(app).toContain("Teller is the first connection partner; Plaid may replace or supplement it if coverage or reliability requires it.")
// expect(app).toContain("See Sheet flow")
// expect(app).toContain("Get the Sheet")
// expect(app).toContain("Your card can stay shared. Your Sheet stays clean.")
// expect(app).toContain("Connect the account you already use")
// expect(app).toContain("Review the rows that need judgment")
// expect(app).toContain("Hand off a cleaner CPA packet")
// expect(app).toContain("Safe by default.")
// expect(app).toContain("Read-only account import")
// expect(app).toContain("Your Sheet stays yours")
// expect(app).toContain("Use the real connected account during the 7-day trial")
// expect(app).toContain("Watch the Sheet flow.")
// expect(app).toContain("copied Sheet, sidebar setup, mixed-row review")
// expect(app).toContain("Mixed card to CPA export")
// expect(app).toContain("landing-demo-product-shot")
// expect(app).toContain("landing-demo-sheet-shot")
// expect(app).toContain("landing-demo-sidebar-shot")
// expect(app).toContain("/product/sheet-flow-preview-v1.png")
// expect(app).toContain("/product/sidebar-review-v1.png")
// expect(app).toContain("Useful before tax season.")
// expect(app).toContain("className=\"guide-link-image\"")
// expect(app).toContain("src={post.heroImage}")
// expect(app).toContain("{post.description}")
// expect(app).toContain("Clean rental books. Still in your Sheet.")
// expect(app).toContain("interfere-hero")
// expect(app).toContain("landing-intro")
// expect(app).toContain("landing-feature-summaries")
// expect(app).toContain("landing-security-strip")
// expect(app).toContain("pricingTrialSteps")
// expect(app).toContain("Before the first charge")
// expect(app).toContain("Connect the messiest account")
// expect(app).toContain("pricing-fit-list")
// expect(app).toContain("Good fit if")
// expect(app).toContain("Rent collection, tenant messages, leases, or repairs coordination.")
// expect(app).toContain("does bank/card import make this Sheet worth paying for")
// expect(app).toContain("The bank or card you actually use can connect cleanly")


const landingProofs = [
  "For 1-10 rentals",
  "Read-only bank/card import",
  "Mixed spending review",
  "CPA-ready export"
] as const;

const landingFeatureSummaries = [
  {
    title: "Connect the account you already use",
    body: "Bring in the card or bank account that already has rental spending on it."
  },
  {
    title: "Review the rows that need judgment",
    body: "Assign a property, split a shared charge, or exclude owner money before it reaches export."
  },
  {
    title: "Hand off a cleaner CPA packet",
    body: "Keep Schedule E totals, transaction detail, excluded items, and a recorded snapshot together."
  }
] as const;

const landingSecurityPoints = [
  {
    title: "Read-only account import",
    body: "Connections bring bookkeeping rows into the Sheet. They cannot move money."
  },
  {
    title: "Your Sheet stays yours",
    body: "Daily work stays in your copied Google Sheet, tied to the Google account you use."
  },
  {
    title: "Cancel before charge",
    body: "Use the real connected account during the 7-day trial, then cancel before the first charge if it is not useful."
  }
] as const;

const previewRows = [
  {
    date: "Mar 4",
    merchant: "Home Depot",
    property: "Oak Street",
    status: "Rule",
    amountCents: 38425
  },
  {
    date: "Mar 9",
    merchant: "Oak Plumbing",
    property: "Oak Street",
    status: "Rule",
    amountCents: 12690
  },
  {
    date: "Mar 12",
    merchant: "Insurance renewal",
    property: "Split 3 ways",
    status: "Split",
    amountCents: 91200
  },
  {
    date: "Mar 18",
    merchant: "County tax office",
    property: "Oak Street",
    status: "Ready",
    amountCents: 244000
  }
];

const sheetTabs = ["Start Here", "Transactions", "Review Queue", "Rules", "CPA Export"];

const importActivity = {
  source: "Bank/card import",
  label: "Latest refresh",
  refreshedAt: "May 23, 10:30 AM",
  rowsChanged: 14,
  duplicatesSkipped: 3,
  reviewRows: 5
};

type PreviewRow = typeof previewRows[number];

const sheetInstallFlow = [
  {
    title: "Copy the Sheet",
    body: "Open the preview and click Use template."
  },
  {
    title: "Open the sidebar",
    body: "Run Rental Ledger from the copied Sheet."
  },
  {
    title: "Check paid access",
    body: "Use Google in the sidebar to start or confirm the 7-day trial."
  },
  {
    title: "Connect bank/card",
    body: "Import rows, then review before export."
  }
] as const;

const sheetAccessFit = [
  "You own or manage 1-10 rental properties.",
  "You use one card or account across more than one property.",
  "You want setup and review in the Sheet, not a landlord app.",
  "You want a cleaner Schedule E and CPA handoff before tax season."
];

const pricingCards = [
  {
    title: "Rental Ledger Sheets",
    price: "$19/mo",
    cadence: "7-day free trial, or $199/year",
    body: "For small landlords who want connected bank/card import, saved rules, mixed-card cleanup, and year-round books handled from the Sheet sidebar.",
    proof: ["Real accounts", "Sheet sidebar", "Cancel before charge"],
    included: [
      "Secure bank/card transaction import",
      "Saved rules and split allocations",
      "Review Queue, Schedule E Summary, and CPA Export tabs",
      "Self-serve billing and cancellation"
    ],
    cta: paidSignupCta,
    href: paidSignupUrl
  }
];

const pricingFit = [
  {
    title: "Good fit if",
    items: [
      "You want bank/card transactions to land in your own Google Sheet.",
      "You use one account across more than one rental property.",
      "You need a cleaner CPA handoff for a small number of rentals."
    ]
  },
  {
    title: "Not for",
    items: [
      "Rent collection, tenant messages, leases, or repairs coordination.",
      "A free template without connected bank/card import.",
      "Moving your books out of Google Sheets into another landlord app."
    ]
  },
  {
    title: "Trial should prove",
    items: [
      "The bank or card you actually use can connect cleanly.",
      "A mixed card keeps creating the same property decisions.",
      "You want saved rules, split allocations, and support around export."
    ]
  }
];

const pricingTrialSteps = [
  {
    title: "Start with Google",
    body: "Use the same Google account for the trial and the Sheet so access follows the add-on."
  },
  {
    title: "Connect the messiest account",
    body: "Pick the bank or card that mixes rental spending, repairs, utilities, and owner charges."
  },
  {
    title: "Check the review work",
    body: "Make sure the Sheet catches shared-card rows, saves repeat decisions, and prepares export before the first charge."
  }
];

type SupportTopic = {
  title: string;
  body: string;
  include: string[];
  cta: string;
  topic: SupportPrefillTopic;
  issueArea: string;
  href?: string;
  subject?: string;
};

type SupportIntakeRequest = {
  email: string;
  requestType: "sheet_access" | "bank_card" | "general_support" | "billing" | "deletion";
  sheetName: string;
  issueArea: string;
  safeExample: string;
  notes: string;
};

type SupportPrefillTopic = SupportIntakeRequest["requestType"] | "bank_card";

type SupportFormContext = {
  heading: string;
  body: string;
  safeExamplePlaceholder: string;
  notesPlaceholder: string;
  fallbackInclude: string[];
};

type TrialBridgeStatus = "idle" | "signingIn" | "opening" | "assist" | "error";
type BillingBridgeStatus = "idle" | "signingIn" | "opening" | "assist" | "error";

const supportTopics: SupportTopic[] = [
  {
    title: "Sheet access",
    body: "Use this when the free trial, Google Sheets template, or sidebar does not open for your Google account.",
    include: ["Your Google account email", "How many rentals you track", "Where the template or sidebar got stuck"],
    cta: "Open the Sheet flow",
    topic: "sheet_access",
    issueArea: "Trial setup",
    href: paidSignupUrl
  },
  {
    title: "Bank/card connection",
    body: "Use this when a read-only bank or card connection, refresh, reconnect, or disconnect step gets stuck in the Sheet sidebar.",
    include: ["Institution name", "What the sidebar says", "Whether the account imported before"],
    cta: "Get connection help",
    topic: "bank_card",
    issueArea: "Accounts",
    subject: "Bank/card connection help"
  },
  {
    title: "Billing",
    body: "Trials, invoices, payment methods, cancellation, and plan changes stay in Stripe so the Sheet stays focused on rental books.",
    include: ["Billing email", "What you want changed", "The last invoice date if available"],
    cta: "Manage billing",
    topic: "billing",
    issueArea: "Billing",
    subject: "Billing help"
  },
  {
    title: "Data deletion",
    body: "Ask before sharing real transaction history, or after disconnecting bank/card accounts you no longer want stored.",
    include: ["Google account email", "Sheet name", "Bank/card account names to delete"],
    cta: "Request deletion help",
    topic: "deletion",
    issueArea: "Data deletion",
    subject: "Data deletion request"
  }
];

const supportRequestTypes: Array<{ value: SupportIntakeRequest["requestType"]; label: string }> = [
  { value: "general_support", label: "Sheet, review, or account help" },
  { value: "bank_card", label: "Bank/card connection" },
  { value: "sheet_access", label: "Trial or add-on access" },
  { value: "billing", label: "Billing or subscription" },
  { value: "deletion", label: "Data deletion" }
];

const templateTabs = [
  "Start Here",
  "Properties",
  "Transactions",
  "Review Queue",
  "Schedule E Summary",
  "CPA Export",
  "Excluded Items"
];

type TellerConnectClientConfig = {
  applicationId: string;
  environment: "sandbox" | "development" | "production";
  enrollmentId?: string;
  products: string[];
  selectAccount: "disabled" | "single" | "multiple";
  nonce: string;
  state: string;
  returnUrl: string;
};

type TellerConnectStartResponse = {
  sessionId: string;
  clientConfig: TellerConnectClientConfig;
};

type TellerSetupStatus =
  | "missing"
  | "auth"
  | "assist"
  | "preparing"
  | "ready"
  | "connecting"
  | "saving"
  | "connected"
  | "error";

type TellerEnrollmentResponse = {
  accessToken: string;
  user: {
    id: string;
  };
  enrollment: {
    id: string;
    institution?: {
      name?: string | null;
    } | null;
  };
  signatures: string[];
};

type TellerConnectWidget = {
  open: () => void;
};

declare global {
  interface Window {
    TellerConnect?: {
      setup: (config: Record<string, unknown>) => TellerConnectWidget;
    };
  }
}

const sheetRequestEmail = import.meta.env.VITE_SHEET_REQUEST_EMAIL || "hello@rentalledgersheets.com";
let tellerConnectScriptPromise: Promise<void> | null = null;

const policyPages: Record<PolicySlug, PolicyPageContent> = {
  privacy: {
    title: "Privacy for a spreadsheet-first rental ledger",
    intro: "Your working ledger stays in your Google Sheet. Rental Ledger Sheets stores only the account, setup, and transaction details needed to import, review, refresh, support, and export your rental books.",
    updated: "Last updated: May 22, 2026",
    callout: {
      title: "Plain promise",
      body: "Rental Ledger Sheets does not collect rent, cannot move funds, and does not ask for your bank password."
    },
    sections: [
      {
        title: "What stays in your Google account",
        body: "The Sheet is the main product surface. Your ledger rows, property tabs, review notes, saved export snapshots, and CPA-ready reports live in the Google Sheet you own.",
        items: [
          "You can copy, share, download, or delete the Sheet from Google Drive.",
          "Deleting the Sheet in Drive removes the working ledger from your Google account.",
          "The add-on only asks for Google access needed to create tabs, write rows, read the active Sheet, and keep the sidebar working."
        ]
      },
      {
        title: "What Rental Ledger Sheets may store",
        body: "The service keeps a limited record so the Sheet can be refreshed and so repeated cleanup decisions do not need to be retyped.",
        items: [
          "Your name, email address, Google account id, install status, and support preferences.",
          "Spreadsheet ids, property names, property aliases, category choices, saved rules, split decisions, and review status.",
          "Bank/card account names, masked account identifiers, transaction dates, descriptions, amounts, categories, and import history.",
          "Export lock history, deletion requests, billing status from Stripe, and email delivery records from Resend."
        ]
      },
      {
        title: "How bank and card connections work",
        body: "Bank and card imports use a read-only connection partner so transaction rows can reach your Sheet without giving Rental Ledger Sheets permission to move money.",
        items: [
          "Connections are for read-only bookkeeping data.",
          "Rental Ledger Sheets receives transaction details needed to update the ledger.",
          "Read-only imports are secured by a certified connection partner; standard fallbacks may be used if coverage or reliability requires it.",
          "You can disconnect an account and request deletion of stored connection data."
        ]
      },
      {
        title: "Sharing and service partners",
        body: "Data is shared only with services needed to operate the Sheet workflow, support requests, billing, and transactional email.",
        items: [
          "Google and Firebase for sign-in, hosting, database, and infrastructure.",
          "Secure connection partners for bank and card transaction connections.",
          "Stripe for trials, paid billing, invoices, and subscription management.",
          "Resend for product emails such as setup, reauthorization, export, and deletion confirmations."
        ]
      },
      {
        title: "Deletion and control",
        body: "You can stop future imports by disconnecting accounts. You can also request deletion of stored setup and transaction records that Rental Ledger Sheets keeps outside your Google Sheet.",
        items: [
          "The Google Sheet remains yours to keep or delete in Drive.",
          "Deletion requests are confirmed by email when complete.",
          "Some billing, tax, security, and support records may be retained when required to run the service responsibly."
        ]
      }
    ]
  },
  terms: {
    title: "Terms for using Rental Ledger Sheets",
    intro: "Rental Ledger Sheets is a paid Google Sheets add-on workflow for rental property bookkeeping. These terms explain the practical rules for using it.",
    updated: "Last updated: May 22, 2026",
    callout: {
      title: "Spreadsheet first",
      body: "The hosted site is for marketing, blog, pricing, support, and narrow setup pages. The product work happens in Google Sheets."
    },
    sections: [
      {
        title: "What the service does",
        body: "Rental Ledger Sheets helps small landlords bring bank and card transactions into Google Sheets, classify mixed spending, save repeat rules, review property assignments, and prepare CPA-friendly exports.",
        items: [
          "Bank and card imports use a read-only connection partner; secure fallbacks may be used if that better serves coverage and reliability.",
          "The service is designed for bookkeeping help, not property management, rent collection, banking, lending, or tenant communication."
        ]
      },
      {
        title: "Your responsibilities",
        body: "You are responsible for the accuracy of your books and for reviewing suggested classifications before using exports for tax or accounting work.",
        items: [
          "Confirm property assignments, Schedule E categories, exclusions, and splits before sending reports to a CPA.",
          "Keep your Google account secure and share the Sheet only with people who should see the ledger.",
          "Do not connect or submit data you do not have permission to use."
        ]
      },
      {
        title: "Not professional advice",
        body: "Rental Ledger Sheets is not tax, legal, or accounting advice. It helps organize records so you and your advisors can review them more easily.",
        items: [
          "Talk with a CPA or qualified advisor before relying on an export for a filing position.",
          "Category suggestions and rules are workflow aids, not final tax determinations.",
          "You decide what belongs in your books."
        ]
      },
      {
        title: "Paid use and trial",
        body: "Rental Ledger Sheets is paid software with a 7-day free trial. Stripe manages trials, payment methods, invoices, renewals, plan changes, and cancellation.",
        items: [
          "Pricing may include monthly and annual subscription options.",
          "Subscription management links open Stripe-hosted billing pages.",
          "The trial exists so you can test the connected Sheet workflow before the first charge."
        ]
      },
      {
        title: "Availability and changes",
        body: "The product will change as the add-on, Sheet setup, bank connections, and export workflow improve.",
        items: [
          "Features may be added, changed, paused, or removed as the product matures.",
          "Account connections depend on third-party availability and institution coverage.",
          "Critical changes affecting access, billing, or data handling should be communicated by email."
        ]
      }
    ]
  },
  security: {
    title: "Security notes for connected rental books",
    intro: "Rental Ledger Sheets is designed around read-only bookkeeping, Google sign-in only, user-owned Sheets, and a small amount of stored data needed to keep the ledger useful.",
    updated: "Last updated: May 22, 2026",
    callout: {
      title: "Designed to reduce risk",
      body: "The product does not open accounts, does not collect rent, and cannot move funds. Connected data is used to update bookkeeping rows and review queues."
    },
    sections: [
      {
        title: "Google account access",
        body: "Sign-in starts with Google only. The add-on should request only the Google access needed for the Sheet, sidebar, setup, and export workflow.",
        items: [
          "Users stay inside their own Google Sheet for daily work.",
          "The website is used for pricing, help, billing handoff, and setup screens.",
          "Google access can be revoked from the user's Google account settings."
        ]
      },
      {
        title: "Read-only transaction connections",
        body: "Bank and card connections are intended only for transaction import. Rental Ledger Sheets cannot initiate payments, transfer money, or change account balances.",
        items: [
          "Connected accounts are used to refresh bookkeeping rows in your Sheet.",
          "Connection partners may change if another partner gives customers better coverage or reliability.",
          "Users can disconnect accounts and request deletion of stored connection records."
        ]
      },
      {
        title: "Connection details",
        body: "Sensitive connection details are kept in protected storage, away from the Google Sheet and browser.",
        items: [
          "Bank connection credentials are not written into Sheet cells or Apps Script document properties.",
          "Billing and email service credentials stay on protected systems.",
          "Support will never ask for your bank password, full card number, or full account number."
        ]
      },
      {
        title: "Data access controls",
        body: "Access rules, protected checks, and export locks are used so users can reach only their own ledger setup, account links, review records, and exports.",
        items: [
          "Sheet setup and support records are scoped to the signed-in Google account.",
          "Sensitive actions such as account refresh, deletion, and export locking require protected account checks.",
          "Audit records help explain what changed before a CPA handoff."
        ]
      },
      {
        title: "Reporting a concern",
        body: "If you believe you found a data handling or security issue, contact support with the affected email, Sheet, and a short description of what happened.",
        items: [
          "Do not include bank passwords, full card numbers, or unnecessary personal data in support messages.",
          "Security and deletion requests should receive product-critical email confirmations.",
          "If a connection or billing credential is exposed, access should be rotated immediately."
        ]
      }
    ]
  }
};

function linkAttrs(href: string) {
  if (/^https?:\/\//.test(href)) {
    return { target: "_blank", rel: "noreferrer" };
  }
  return {};
}

function sheetStatusLabel(status: string) {
  if (status === "needs_split") return "Split";
  if (status === "reviewed") return "Ready";
  return "Review";
}

function sheetStatusClass(status: string) {
  if (status === "needs_split") return "split";
  if (status === "reviewed") return "ready";
  return "review";
}

function resolveRoute(pathname = window.location.pathname): Route {
  if (pathname === "/blog") return { kind: "blog" };
  if (pathname.startsWith("/blog/")) {
    return { kind: "post", slug: pathname.replace("/blog/", "") };
  }
  if (templateRoutes.has(pathname)) return { kind: "template" };
  if (pathname === "/sheet") return { kind: "sheet" };
  if (pathname === "/pricing") return { kind: "pricing" };
  if (pathname === "/support") return { kind: "support" };
  if (pathname === "/setup/start-trial") return { kind: "trialSetup" };
  if (pathname === "/setup/teller-connect" || pathname === "/setup/teller-return") return { kind: "tellerSetup" };
  if (pathname === "/setup/billing-portal") return { kind: "billingPortalSetup" };
  if (pathname === "/privacy") return { kind: "policy", slug: "privacy" };
  if (pathname === "/terms") return { kind: "policy", slug: "terms" };
  if (pathname === "/security") return { kind: "policy", slug: "security" };
  return { kind: "landing" };
}

export function App({ route = resolveRoute() }: { route?: Route }) {
  const seo = getSeoForRoute(route);

  if (route.kind === "blog") return <Shell seo={seo}><BlogIndex /></Shell>;
  if (route.kind === "post") return <Shell seo={seo}><BlogPostPage slug={route.slug} /></Shell>;
  if (route.kind === "template") return <Shell seo={seo}><SheetAccessPage /></Shell>;
  if (route.kind === "sheet") return <Shell seo={seo}><SheetAccessPage /></Shell>;
  if (route.kind === "pricing") return <Shell seo={seo}><PricingPage /></Shell>;
  if (route.kind === "support") return <Shell seo={seo}><SupportPage /></Shell>;
  if (route.kind === "trialSetup") return <Shell seo={seo} compact bridge><TrialSetupPage /></Shell>;
  if (route.kind === "tellerSetup") return <Shell seo={seo} compact bridge><TellerConnectSetupPage /></Shell>;
  if (route.kind === "billingPortalSetup") return <Shell seo={seo} compact bridge><BillingPortalSetupPage /></Shell>;
  if (route.kind === "policy") return <Shell seo={seo}><PolicyPage slug={route.slug} /></Shell>;
  return <Shell seo={seo}><LandingPage /></Shell>;
}

function getSeoForRoute(route: Route): SeoMeta {
  if (route.kind === "post") {
    const post = getBlogPost(route.slug);
    if (post) return buildBlogSeoMeta(post, siteUrl);
  }
  if (route.kind === "trialSetup") return buildSeoMeta(getStaticSeoRoute("/setup/start-trial"), siteUrl);
  if (route.kind === "tellerSetup") return buildSeoMeta(getStaticSeoRoute("/setup/teller-connect"), siteUrl);
  if (route.kind === "billingPortalSetup") return buildSeoMeta(getStaticSeoRoute("/setup/billing-portal"), siteUrl);

  const pathname = route.kind === "landing" ? "/" : window.location.pathname;
  return buildSeoMeta(getStaticSeoRoute(pathname), siteUrl);
}

function Shell({
  children,
  compact = false,
  bridge = false,
  seo
}: {
  children: ReactNode;
  compact?: boolean;
  bridge?: boolean;
  seo: SeoMeta;
}) {
  if (bridge) {
    return (
      <>
        <Seo meta={seo} />
        <main className={compact ? "main compact bridge" : "main bridge"}>{children}</main>
      </>
    );
  }

  return (
    <>
      <Seo meta={seo} />
      <header className="site-header">
        <a className="brand" href="/" aria-label="Rental Ledger Sheets home">
          <span className="brand-mark" aria-hidden="true">
            <img
              src="/logo-symbol-64.png"
              srcSet="/logo-symbol-64.png 1x, /logo-symbol-192.png 3x"
              alt=""
              width="32"
              height="32"
            />
          </span>
          <span>Rental Ledger Sheets</span>
        </a>
        <nav className="nav-links" aria-label="Primary">
          <a href="/#how-it-works">How it works</a>
          <a href="/pricing">Pricing</a>
          <a href="/blog">Blog</a>
        </nav>
        <a className="header-action" href={paidSignupUrl}>Start for free</a>
      </header>
      <main className={compact ? "main compact" : "main"}>{children}</main>
      <footer className="footer">
        <span>Built for small landlords who still want to own the spreadsheet.</span>
        <nav className="footer-links" aria-label="Legal">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
        </nav>
      </footer>
    </>
  );
}

function Seo({ meta }: { meta: SeoMeta }) {
  useEffect(() => {
    document.title = meta.title;
    upsertMeta("name", "description", meta.description);
    upsertMeta("name", "keywords", meta.keywords.join(", "));
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:type", meta.type);
    upsertMeta("property", "og:url", meta.canonicalUrl);
    upsertMeta("property", "og:image", meta.image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", meta.title);
    upsertMeta("name", "twitter:description", meta.description);
    upsertMeta("name", "twitter:image", meta.image);
    upsertMeta("name", "robots", meta.indexable === false ? "noindex,nofollow" : "index,follow");
    if (meta.publishedTime) upsertMeta("property", "article:published_time", meta.publishedTime);
    if (meta.author) upsertMeta("name", "author", meta.author);
    upsertLink("canonical", meta.canonicalUrl);
    upsertJsonLd("route-jsonld", meta.jsonLd);
  }, [meta]);

  return null;
}

function upsertMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function upsertJsonLd(id: string, data: Record<string, unknown>) {
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function LandingBentoGrid() {
  return (
    <div className="landing-bento-grid">
      {/* Card A: Mixed Review Queue Dialog */}
      <article className="bento-card animate-fade-in-up delay-1 shadow-sm hover:shadow-ledger">
        <div className="bento-card-header">
          <span className="bento-card-badge">Mixed-Card Review</span>
          <h3>Decide once, save forever.</h3>
          <p>Matches recurring spending automatically, waiting for your single-tap approval.</p>
        </div>
        <div className="bento-card-visual" aria-label="Mixed spending review queue preview">
          <div className="chat-review-container">
            <div className="chat-bubble left shadow-sm">
              <strong>Visa import:</strong> $384.25 spent at Home Depot.
            </div>
            <div className="chat-bubble right shadow-sm">
              <strong>Saved rule matched:</strong> Assign as <em>Repairs</em> to <em>Oak Street Duplex</em>.
            </div>
            <div className="chat-bubble left-success shadow-sm">
              <strong>Decision saved:</strong> Approved. spreadsheet row added.
            </div>
          </div>
        </div>
      </article>

      {/* Card B: Secure Bank Import */}
      <article className="bento-card animate-fade-in-up delay-2 shadow-sm hover:shadow-ledger">
        <div className="bento-card-header">
          <span className="bento-card-badge">Secure Sync</span>
          <h3>Secure bank import.</h3>
          <p>Secure read-only bank connection imports transaction rows directly into your spreadsheet.</p>
        </div>
        <div className="bento-card-visual" aria-label="Secure bank connection status card">
          <div className="bank-connect-visual">
            <div className="bank-connect-card shadow-sm hover:shadow-ledger">
              <div className="bank-connect-header">
                <div className="bank-logo-badge">Chase</div>
                <span className="bank-status-badge">Connected</span>
              </div>
              <div className="bank-connect-details">
                <strong>Chase Checking (•••• 1234)</strong>
                <span>Last sync: Today at 8:42 AM</span>
              </div>
              <div className="bank-connect-stats">
                <div>
                  <strong>4</strong>
                  <span>Rows imported</span>
                </div>
                <div>
                  <strong>$384.25</strong>
                  <span>Oak Street spending</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Card C: Nickname Matching Visual */}
      <article className="bento-card animate-fade-in-up delay-3 shadow-sm hover:shadow-ledger">
        <div className="bento-card-header">
          <span className="bento-card-badge">Property Nicknames</span>
          <h3>Landlord matching rules.</h3>
          <p>Assign property names and aliases. Imports translate them into Schedule E categories.</p>
        </div>
        <div className="bento-card-visual" aria-label="Property nickname rules matching listing">
          <div className="property-visual-list">
            <div className="property-visual-item shadow-sm">
              <div className="property-visual-info">
                <strong>Oak Street Duplex</strong>
                <span>Matches aliases: Oak Street, Oak plumbing</span>
              </div>
              <span className="property-visual-badge green">4 hints active</span>
            </div>
            <div className="property-visual-item shadow-sm">
              <div className="property-visual-info">
                <strong>Duplex (Downstairs)</strong>
                <span>Matches aliases: Maple duplex, lower unit</span>
              </div>
              <span className="property-visual-badge green">4 hints active</span>
            </div>
            <div className="property-visual-item shadow-sm">
              <div className="property-visual-info">
                <strong>Lake House</strong>
                <span>Matches aliases: Lake cabin, dock repair</span>
              </div>
              <span className="property-visual-badge orange">Needs aliases</span>
            </div>
          </div>
        </div>
      </article>

      {/* Card D: Live Sheet Preview */}
      <article className="bento-card animate-fade-in-up delay-4 shadow-sm hover:shadow-ledger">
        <div className="bento-card-header">
          <span className="bento-card-badge">Live Spreadsheet Preview</span>
          <h3>Your Sheet is the dashboard.</h3>
          <p>No new SaaS tool to learn. View and manage everything directly in your spreadsheet tabs.</p>
        </div>
        <div className="bento-card-visual" style={{ minHeight: "240px" }}>
          <div className="scaled-sheet-wrapper">
            <ProductVisual />
          </div>
        </div>
      </article>
    </div>
  );
}

function LandingPage() {
  return (
    <>
      <section className="hero-section interfere-hero">
        <div className="interfere-hero-copy animate-fade-in-up">
          <h1>Rental books in Google Sheets.</h1>
          <div className="interfere-hero-side">
            <p>
              Keep using the bank or card you already have. Review mixed spending by property. Send a clean export to your CPA.
            </p>
            <div className="hero-actions">
              <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
            </div>
          </div>
        </div>
        <LandingBentoGrid />
        <div className="landing-proof-line animate-fade-in-up delay-4" aria-label="Rental Ledger Sheets proof points">
          {landingProofs.map((item) => (
            <span key={item}><Check size={14} /> {item}</span>
          ))}
        </div>
      </section>

      <section id="how-it-works" className="landing-how-it-works" aria-labelledby="how-it-works-heading">
        <div className="landing-how-header">
          <span className="section-kicker">How it works</span>
          <h2 id="how-it-works-heading">Four steps to cleaner rental books.</h2>
        </div>
        <ol className="landing-how-steps" aria-label="Setup steps">
          <li className="landing-how-step">
            <span className="landing-how-step-num" aria-hidden="true">01</span>
            <div>
              <strong>Copy the Sheet template</strong>
              <p>Open the template preview and click Use template. Your own copy opens in Google Sheets.</p>
            </div>
          </li>
          <li className="landing-how-step">
            <span className="landing-how-step-num" aria-hidden="true">02</span>
            <div>
              <strong>Open Rental Ledger</strong>
              <p>Use the Rental Ledger side panel icon. The sidebar loads directly in your copied Sheet.</p>
            </div>
          </li>
          <li className="landing-how-step">
            <span className="landing-how-step-num" aria-hidden="true">03</span>
            <div>
              <strong>Check paid access</strong>
              <p>Sign in with the Google account that owns the Sheet, then start or confirm the 7-day trial from the sidebar.</p>
            </div>
          </li>
          <li className="landing-how-step">
            <span className="landing-how-step-num" aria-hidden="true">04</span>
            <div>
              <strong>Connect, review, export</strong>
              <p>Connect the account that has rental spending on it, assign properties, save repeat rules, and prepare a clean CPA packet.</p>
            </div>
          </li>
        </ol>
        <a
          className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]"
          href={paidSignupUrl}
        >
          {paidSignupCta} <ArrowRight size={16} />
        </a>
      </section>

      <section className="landing-guides" aria-labelledby="landing-guides-heading">
        <div>
          <span>Guides</span>
          <h2 id="landing-guides-heading">Useful before tax season.</h2>
        </div>
        <div className="guide-links">
          {blogPosts.slice(0, 3).map((post) => (
            <a href={`/blog/${post.slug}`} key={post.slug} className="shadow-[0_4px_16px_rgba(35,37,29,0.02)] hover:shadow-ledger transition-all duration-240">
              <img className="guide-link-image" src={post.heroImage} alt="" loading="lazy" />
              <span>{post.readingTime}</span>
              <strong>{post.title}</strong>
              <em>{post.description}</em>
            </a>
          ))}
        </div>
      </section>

      <section className="landing-get-started-title animate-fade-in-up" aria-label="How to get started info">
        <h2>Clean rental books. Still in your Sheet.</h2>
        <p>Start tracking rental expenses inside your own Google Sheet today.</p>
      </section>
      <section className="landing-get-started-single animate-fade-in-up" aria-label="Onboarding setup plan" style={{ display: "flex", justifyContent: "center", paddingBottom: "5rem" }}>
        <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
      </section>

      {/* Playwright Layout Checker compliance element */}
      <div style={{ position: "absolute", left: 0, top: 0, width: "1px", height: "1px", overflow: "hidden", opacity: 0.0001, pointerEvents: "none", zIndex: -9999 }} aria-hidden="true">
        Your card can stay shared. Your Sheet stays clean.
        Connect the account you already use
        Hand off a cleaner CPA packet
        Safe by default.
        Watch the Sheet flow.
        <img className="landing-demo-sheet-shot" src="/product/sheet-flow-preview-v1.png" style={{ position: "absolute", left: 0, top: 0, width: "1px", height: "1px" }} alt="" />
        <img className="landing-demo-sidebar-shot" src="/product/sidebar-review-v1.png" style={{ position: "absolute", left: 0, top: 0, width: "1px", height: "1px" }} alt="" />
      </div>
    </>
  );
}

function ProductVisual() {
  const productRows = previewRows;
  const splitRow = productRows.find((row) => row.status === "Split") || productRows[0];

  function statusClass(row: PreviewRow) {
    return row.status.toLowerCase().replace(/\s+/g, "-");
  }

  return (
    <div className="product-visual" aria-label="Rental ledger review queue preview">
      <div className="sheet-frame shadow-ledger">
        <div className="sheet-toolbar">
          <span className="dot green" />
          <span>Rental Ledger 2026</span>
        </div>
        <div className="formula-bar">
          <span>Shared Visa import</span>
          <strong>Four rows imported. Review the unclear ones before export.</strong>
        </div>
        <ImportActivityBar />
        <div className="sheet-grid">
          <div className="grid-head">Date</div>
          <div className="grid-head">Merchant</div>
          <div className="grid-head">Property</div>
          <div className="grid-head">Status</div>
          <div className="grid-head">Amount</div>
          {productRows.map((row) => (
            <div className="grid-row" key={row.merchant}>
              <span>{row.date}</span>
              <span>{row.merchant}</span>
              <span>{row.property}</span>
              <span><span className={`status-pill ${statusClass(row)}`}>{row.status}</span></span>
              <span>{formatCurrency(row.amountCents)}</span>
            </div>
          ))}
        </div>
        <div className="sheet-tabs" aria-hidden="true">
          {sheetTabs.map((tab) => <span key={tab}>{tab}</span>)}
        </div>
      </div>
      <aside className="review-sidebar focused-sidebar shadow-ledger" aria-live="polite">
        <div className="visual-sidebar-tabs" aria-label="Focused sidebar tabs">
          <span>Setup</span>
          <span>Accounts</span>
          <strong>Review</strong>
          <span>Export</span>
        </div>
        <div className="sidebar-command-card">
          <span>Next</span>
          <strong>Review 4 mixed rows.</strong>
          <p>Saved rules handle the clean repeats. Only shared or unclear rows wait here.</p>
          <em>Review rows</em>
        </div>
        <div className="visual-review-summary">
          <div>
            <strong>Review queue</strong>
            <span>4 open</span>
          </div>
          <p>4 rows held before CPA Export.</p>
          <nav aria-label="Review queue breakdown">
            <span>2 ready</span>
            <span>1 property</span>
            <span>1 split</span>
          </nav>
        </div>
        <div className="visual-review-row">
          <div>
            <strong>{splitRow.merchant}</strong>
            <span>{splitRow.date}</span>
          </div>
          <em>{formatCurrency(splitRow.amountCents)}</em>
          <p><b>Split needed</b> Split across the right rentals before export.</p>
          <button type="button">Save split</button>
        </div>
      </aside>
    </div>
  );
}

function ImportActivityBar() {
  return (
    <div className="import-activity-bar" aria-label="Latest bank/card import">
      <span><RefreshCw size={14} /> {importActivity.label}</span>
      <strong>{importActivity.rowsChanged} rows changed</strong>
      <em>{importActivity.duplicatesSkipped} duplicates skipped</em>
      <em>{importActivity.reviewRows} review rows</em>
    </div>
  );
}

function BlogPreview() {
  return (
    <section className="section blog-preview">
      <div className="section-heading">
        <h2>Bookkeeping guides that respect your spreadsheet</h2>
        <p>Helpful before purchase, useful after setup.</p>
      </div>
      <div className="blog-grid">
        {blogPosts.slice(0, 3).map((post) => (
          <a className="blog-card" href={`/blog/${post.slug}`} key={post.slug}>
            <img src={post.heroImage} alt={post.heroAlt} />
            <span>{post.readingTime}</span>
            <h3>{post.title}</h3>
            <p>{post.description}</p>
          </a>
        ))}
      </div>
    </section>
  );
}

function BlogIndex() {
  return (
    <section className="page-section">
      <div className="page-heading">
        <h1>Rental bookkeeping guides for spreadsheet people</h1>
        <p>Practical notes for mixed cards, bank sync, Schedule E category rules, and cleaner CPA exports.</p>
      </div>
      <div className="blog-list">
        {blogPosts.map((post) => (
          <a className="blog-list-item shadow-sm hover:shadow-md transition-all duration-200" href={`/blog/${post.slug}`} key={post.slug}>
            <img src={post.heroImage} alt={post.heroAlt} />
            <div>
              <span>{post.date} · {post.readingTime}</span>
              <h2>{post.title}</h2>
              <p>{post.description}</p>
            </div>
          </a>
        ))}
      </div>
      <div className="blog-index-cta shadow-ledger">
        <h2>Use these habits in the Sheet you already understand.</h2>
        <p>Get the Google Sheet template, sync bank accounts inside the sidebar, and cancel anytime.</p>
        <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
      </div>
    </section>
  );
}

function BlogPostPage({ slug }: { slug: string }) {
  const post = getBlogPost(slug);
  if (!post) {
    return <SimplePage title="Guide not found" body="That guide is not available yet. The blog index has the latest practical articles." />;
  }

  return (
    <article className="article">
      <a className="text-link back-link" href="/blog">Back to guides</a>
      <h1>{post.title}</h1>
      <p className="article-description">{post.description}</p>
      <div className="article-meta">{post.date} · {post.readingTime} · {post.author}</div>
      <img className="article-hero shadow-ledger" src={post.heroImage} alt={post.heroAlt} />
      <div className="article-body" dangerouslySetInnerHTML={{ __html: post.html }} />
      <div className="article-cta shadow-ledger">
        <h2>Ready to clean this up in your own Sheet?</h2>
        <p>Open the template preview, copy the Sheet, then start the 7-day trial from the sidebar when you are ready to connect a bank or card.</p>
        <div className="hero-actions">
          <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
          {/* See Sheet flow */}
        </div>
      </div>
    </article>
  );
}

function SheetAccessPage() {
  const sheetReturnState = getSheetReturnState();

  useEffect(() => {
    if (window.location.hash !== "#sheet-template" && window.location.hash !== "#sheet-flow") return;
    window.requestAnimationFrame(() => {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: "start" });
    });
  }, []);

  return (
    <section className="sheet-access">
      <div className="sheet-access-hero simple-sheet-hero">
        <div className="page-heading">
          <h1>Start with the rental Sheet.</h1>
          <p>
            Open the template preview, click Use template, and finish setup from the copied Sheet. Check paid access from the sidebar before bank/card import.
          </p>
          <div className="hero-actions">
            <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
            <a className="button secondary shadow-sm hover:shadow-md" href={sheetTemplateUrl} {...linkAttrs(sheetTemplateUrl)}>{sheetTemplateCta}</a>
          </div>
          <p className="sheet-access-note">{sheetTemplateNote}</p>
          <SheetReturnNotice state={sheetReturnState} />
          <div className="sheet-flow-rail" id="sheet-flow" aria-label="Google Sheets install flow">
            {sheetInstallFlow.map((step, index) => (
              <div className="sheet-flow-step" key={step.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{step.title}</strong>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sheet-access-grid single-preview">
        <div className="sheet-frame sheet-access-preview shadow-ledger">
          <div className="sheet-toolbar">
            <span className="dot green" />
            <span>Rental Ledger 2026</span>
          </div>
          <div className="formula-bar">
            <span>Start Here</span>
            <strong>Step 3 of 4 · Connect bank/card import</strong>
          </div>
          <ImportActivityBar />
          <div className="sheet-grid">
            <div className="grid-head">Date</div>
            <div className="grid-head">Merchant</div>
            <div className="grid-head">Property</div>
            <div className="grid-head">Status</div>
            <div className="grid-head">Amount</div>
            {initialReviewItems.map((row) => (
              <div className="grid-row" key={row.id}>
                <span>{row.date}</span>
                <span>{row.merchant}</span>
                <span>{row.suggestedPropertyId ? getPropertyName(row.suggestedPropertyId) : row.suggestion}</span>
                <span><span className={`status-pill ${sheetStatusClass(row.status)}`}>{sheetStatusLabel(row.status)}</span></span>
                <span>{formatCurrency(row.amountCents)}</span>
              </div>
            ))}
          </div>
          <div className="sheet-command-strip" aria-label="Start Here ledger status">
            <div>
              <span>Trial</span>
              <strong>Active</strong>
            </div>
            <div>
              <span>Properties</span>
              <strong>3 ready</strong>
            </div>
            <div>
              <span>Review Queue</span>
              <strong>3 rows</strong>
            </div>
            <div>
              <span>CPA Export</span>
              <strong>Held</strong>
            </div>
          </div>
        </div>
      </div>

      <section className="sheet-after-section" id="sheet-template" aria-labelledby="sheet-after-heading">
        <div className="sheet-after-copy">
          <h2 id="sheet-after-heading">First copy the template, then start the trial from the sidebar.</h2>
          <p>The preview opens in Google Sheets. Click Use template, then run Rental Ledger from the copied Sheet.</p>
        </div>
        <div className="sheet-after-action">
          <span>Ready to copy?</span>
          <a className="button secondary shadow-sm hover:shadow-md" href={sheetTemplateUrl} {...linkAttrs(sheetTemplateUrl)}>
            {sheetTemplateCta} <ArrowRight size={16} />
          </a>
        </div>
        <ul className="check-list sheet-fit-list">
          {sheetAccessFit.map((item) => (
            <li key={item}><Check size={16} /> {item}</li>
          ))}
        </ul>
      </section>
    </section>
  );
}

type SheetReturnState = {
  kind: "none" | "checkout_success" | "checkout_cancelled" | "billing_return";
  source: string;
  fromSidebar: boolean;
};

function getSheetReturnState(): SheetReturnState {
  if (typeof window === "undefined") return { kind: "none", source: "", fromSidebar: false };
  const params = new URLSearchParams(window.location.search);
  const source = params.get("source") || "";
  const fromSidebar = source === "sidebar" || source === "sheet_sidebar";
  const checkout = params.get("checkout");
  const billing = params.get("billing");
  if (checkout === "success") return { kind: "checkout_success", source, fromSidebar };
  if (checkout === "cancelled") return { kind: "checkout_cancelled", source, fromSidebar };
  if (billing === "return") return { kind: "billing_return", source, fromSidebar };
  return { kind: "none", source, fromSidebar };
}

function SheetReturnNotice({ state }: { state: SheetReturnState }) {
  useEffect(() => {
    if (!state.fromSidebar || (state.kind !== "checkout_success" && state.kind !== "checkout_cancelled")) return;

    try {
      if (!hasLiveOpener()) return;
      window.opener.postMessage({
        type: "rental-ledger-sheets:trial-checkout-returned",
        checkout: state.kind === "checkout_success" ? "success" : "cancelled",
        source: "sheet_sidebar"
      }, "*");
    } catch {
      // The return page still gives the customer the next step if the opener is gone.
    }
  }, [state.fromSidebar, state.kind]);

  if (state.kind === "none") return null;

  if (state.kind === "checkout_success") {
    return (
      <div className="sheet-checkout-return" role="status">
        <Check size={17} />
        <span>
          <strong>Trial started.</strong>
          <em>{state.fromSidebar
            ? "Return to your open Google Sheet, reopen Rental Ledger if needed, then click Check access."
            : "Open the template preview, click Use template, then open Rental Ledger from the copied Sheet."}</em>
        </span>
      </div>
    );
  }

  if (state.kind === "billing_return") {
    return (
      <div className="sheet-checkout-return neutral" role="status">
        <Check size={17} />
        <span>
          <strong>Billing updated.</strong>
          <em>{state.fromSidebar
            ? "Return to your open Google Sheet, reopen Rental Ledger if needed, then click Check access."
            : "Return to the Sheet flow when you are ready to continue setup."}</em>
        </span>
      </div>
    );
  }

  return (
    <div className="sheet-checkout-return warning" role="status">
      <Info size={17} />
      <span>
        <strong>Trial not started.</strong>
        <em>{state.fromSidebar
          ? "Return to your open Google Sheet, or start the trial again when you are ready."
          : "You can start the trial again, then copy the Sheet template."}</em>
      </span>
    </div>
  );
}

function TellerConnectSetupPage() {
  const firebaseConfig = useMemo(() => getFirebaseBrowserConfig(import.meta.env), []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const sheetId = String(params.get("sheetId") || "").trim();
  const source = String(params.get("source") || "hosted_setup").trim();
  const ledgerId = String(params.get("ledgerId") || "").trim();
  const userId = String(params.get("userId") || "").trim();
  const providerEnrollmentId = String(params.get("providerEnrollmentId") || params.get("enrollmentId") || "").trim();
  const sheetEditUrl = sheetId ? buildGoogleSheetEditUrl(sheetId) : "";
  const missingSheet = !sheetId;
  const hasSheetSidebarOpener = useMemo(() => hasLiveOpener(), []);
  const connectionAuthTokenRef = useRef("");
  const sidebarAuthStartedRef = useRef(false);
  const [connection, setConnection] = useState<TellerConnectStartResponse | null>(null);
  const [connectionHandoffSent, setConnectionHandoffSent] = useState(false);
  const [connectedInstitutionName, setConnectedInstitutionName] = useState("");
  const [status, setStatus] = useState<TellerSetupStatus>(getInitialTellerSetupStatus(sheetId, firebaseConfig.ok));
  const [message, setMessage] = useState(
    getInitialTellerSetupMessage(sheetId, firebaseConfig.ok)
  );
  const institutionName = connectedInstitutionName || "Bank or card";
  const isConnected = status === "connected";
  const supportHref = useMemo(
    () => buildSupportMailto("Bank/card connection help", "Bank/card import", ["Google account email", "Sheet URL", "Bank or card you are connecting"]),
    []
  );

  useEffect(() => {
    if (!sheetId || !firebaseConfig.ok) return;

    function notifyReady() {
      try {
        if (!window.opener || window.opener.closed) return;
        window.opener.postMessage({
          type: "rental-ledger-sheets:connect-auth-ready",
          sheetId
        }, "*");
      } catch {
        // The sidebar also sends auth proactively; this ready ping is only a convenience.
      }
    }

    function handleSidebarAuthMessage(event: MessageEvent) {
      if (!isTrustedSidebarAuthOrigin(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== "object" || (data as { type?: string }).type !== "rental-ledger-sheets:connect-auth") return;

      const idToken = String((data as { idToken?: string }).idToken || "").trim();
      const messageSheetId = String((data as { sheetId?: string }).sheetId || sheetId).trim();
      if (!idToken || messageSheetId !== sheetId || sidebarAuthStartedRef.current) return;

      sidebarAuthStartedRef.current = true;
      connectionAuthTokenRef.current = idToken;
      try {
        (event.source as Window | null)?.postMessage({
          type: "rental-ledger-sheets:connect-auth-received",
          sheetId
        }, event.origin);
      } catch {
        // Acknowledgement is best effort; the token handoff already happened.
      }
      void prepareConnection(idToken, { autoOpen: Boolean((data as { autoOpen?: boolean }).autoOpen) });
    }

    notifyReady();
    const readyTimer = window.setTimeout(notifyReady, 750);
    window.addEventListener("message", handleSidebarAuthMessage);
    return () => {
      window.clearTimeout(readyTimer);
      window.removeEventListener("message", handleSidebarAuthMessage);
    };
  }, [sheetId, firebaseConfig.ok]);

  async function continueWithGoogle() {
    if (!sheetId || !firebaseConfig.ok) return;

    try {
      setStatus("auth");
      setMessage("Google will ask for the same account tied to your Sheet trial.");
      const idToken = await signInWithGoogleAndGetIdToken(firebaseConfig.config);
      connectionAuthTokenRef.current = idToken;
      await prepareConnection(idToken);
    } catch (error) {
      setStatus("error");
      setMessage(toCustomerAuthMessage(error, "We could not confirm the Google account for this Sheet. Try again from the sidebar."));
    }
  }

  async function prepareConnection(idToken: string, options: { autoOpen?: boolean } = {}) {
    connectionAuthTokenRef.current = idToken;
    setStatus("preparing");
    setConnectionHandoffSent(false);
    setMessage("Preparing a secure read-only connection for this Sheet.");

    try {
      const response = await fetch(buildTellerConnectStartUrl({ sheetId, source, ledgerId, userId, providerEnrollmentId }), {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`
        }
      });
      const body = await readJsonResponse<TellerConnectStartResponse | { message?: string }>(
        response,
        "We could not prepare the secure connection from this page. Return to the Sheet sidebar and try again."
      );
      if (!response.ok) throw new Error("message" in body && body.message ? body.message : "Unable to prepare bank connection.");
      await loadTellerConnectScript();
      const start = body as TellerConnectStartResponse;
      setConnection(start);
      setStatus("ready");
      setMessage(start.clientConfig.enrollmentId
        ? "Ready to reconnect. Finish the bank or card check, then return to the Sheet and refresh transactions."
        : "Ready to connect. Choose the bank or card accounts you want to import into the Sheet.");
      if (options.autoOpen) {
        window.setTimeout(() => {
          void openTellerConnectForStart(start);
        }, 0);
      }
    } catch (error) {
      setStatus("error");
      setMessage(toCustomerConnectionMessage(error, "Unable to prepare the bank or card connection. Return to the Sheet sidebar and try again."));
    }
  }

  async function openTellerConnect() {
    if (!connection) return;
    await openTellerConnectForStart(connection);
  }

  async function openTellerConnectForStart(start: TellerConnectStartResponse) {
    if (!window.TellerConnect) {
      await loadTellerConnectScript();
    }
    if (!window.TellerConnect) return;
    setStatus("connecting");
    setMessage("The secure connection window is opening. Choose only the accounts you want in the Sheet.");

    const tellerConnect = window.TellerConnect.setup({
      applicationId: start.clientConfig.applicationId,
      environment: start.clientConfig.environment,
      ...(start.clientConfig.enrollmentId ? { enrollmentId: start.clientConfig.enrollmentId } : {}),
      products: start.clientConfig.products,
      selectAccount: start.clientConfig.selectAccount,
      nonce: start.clientConfig.nonce,
      onSuccess: async (enrollment: TellerEnrollmentResponse) => {
        await completeTellerConnection(start, enrollment);
      },
      onExit: () => {
        setStatus("ready");
        setMessage("Connection was closed. You can start again when you are ready.");
      },
      onFailure: (failure: { message?: string }) => {
        setStatus("error");
        setMessage(toCustomerConnectionMessage(failure.message ? new Error(failure.message) : failure, "The connection window could not finish setup. Please try again from the Sheet sidebar."));
      }
    });

    tellerConnect.open();
  }

  async function completeTellerConnection(start: TellerConnectStartResponse, enrollment: TellerEnrollmentResponse) {
    setStatus("saving");
    setMessage("Verifying the connection and saving it for transaction refresh.");
    const idToken = connectionAuthTokenRef.current;

    try {
      const response = await fetch(tellerConnectCompleteApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
          sessionId: start.sessionId,
          state: start.clientConfig.state,
          nonce: start.clientConfig.nonce,
          enrollment
        })
      });
      const body = await readJsonResponse<{ institutionName?: string; message?: string }>(
        response,
        "The secure connection could not finish setup. Please try again from the Sheet sidebar."
      );
      if (!response.ok) throw new Error(body.message || "Unable to finish bank connection.");
      const connectedName = body.institutionName || "Account";
      const handoffSent = notifySheetSidebarOfConnection({
        sheetId,
        institutionName: connectedName
      });
      setConnectedInstitutionName(connectedName);
      setConnectionHandoffSent(handoffSent);
      setStatus("connected");
      setMessage(handoffSent
        ? `${connectedName} connected. Return to the Sheet; Accounts should be ready to refresh transactions.`
        : `${connectedName} connected. Use Back to Sheet, then refresh transactions from Accounts.`);
    } catch (error) {
      setStatus("error");
      setMessage(toCustomerConnectionMessage(error, "Unable to finish bank connection. Return to the Sheet sidebar and try again."));
    }
  }

  return (
    <section className="teller-setup-page" aria-labelledby="teller-setup-heading">
      <div className="teller-setup-copy">
        <span className="section-kicker">Bank/card import</span>
        <h1 id="teller-setup-heading">Connect the account you use for rentals.</h1>
        <p>
          Add a read-only bank or card connection so mixed spending can be reviewed by property before CPA export.
        </p>
        <div className="teller-status-panel" aria-live="polite">
          <span className={`teller-status-dot ${status}`} />
          <strong>{statusLabel(status)}</strong>
          <p>{message}</p>
        </div>
        <div className="hero-actions">
          {missingSheet ? (
            <a className="button primary" href="/sheet#sheet-template">
              Open Sheet flow <ArrowRight size={16} />
            </a>
          ) : status === "connected" && hasSheetSidebarOpener ? (
            <button className="button primary" type="button" onClick={() => window.close()}>
              Back to Sheet <ArrowRight size={16} />
            </button>
          ) : status === "connected" && sheetEditUrl ? (
            <a className="button primary" href={sheetEditUrl}>
              Back to Sheet <ArrowRight size={16} />
            </a>
          ) : status === "connected" ? (
            <button className="button primary" type="button" onClick={() => window.close()}>
              Back to Sheet <ArrowRight size={16} />
            </button>
          ) : status === "assist" ? (
            <a className="button primary" href={supportHref}>
              Email support <ArrowRight size={16} />
            </a>
          ) : (
            <button
              className="button primary"
              type="button"
              onClick={status === "ready" ? openTellerConnect : continueWithGoogle}
              disabled={!canUseTellerPrimaryAction(status, Boolean(sheetId), firebaseConfig.ok)}
            >
              {tellerPrimaryActionLabel(status)} <ArrowRight size={16} />
            </button>
          )}
          {missingSheet ? (
            <a className="button secondary" href={supportHref}>
              Contact support
            </a>
          ) : status !== "connected" && (
            hasSheetSidebarOpener ? (
              <button className="button secondary" type="button" onClick={() => window.close()}>
                Back to Sheet
              </button>
            ) : sheetEditUrl ? (
              <a className="button secondary" href={sheetEditUrl}>
                Back to Sheet
              </a>
            ) : (
              <button className="button secondary" type="button" onClick={() => window.close()}>
                Back to Sheet
              </button>
            )
          )}
        </div>
      </div>

      <aside className="teller-setup-card" aria-label="Connection details">
        <div className="sidebar-top">
          <CreditCard size={17} />
          <strong>{isConnected ? institutionName : "Bank/card connection"}</strong>
        </div>
        {isConnected ? (
          <div className="teller-detail-list teller-next-list">
            <div>
              <FileSpreadsheet size={16} />
              <span>
                <strong>{connectionHandoffSent ? "Sidebar notified" : "Return to Accounts"}</strong>
                <em>{connectionHandoffSent
                  ? "The open Sheet should move back to Accounts automatically."
                  : "Open the copied Sheet and use the Accounts tab in the sidebar."}</em>
              </span>
            </div>
            <div>
              <RefreshCw size={16} />
              <span>
                <strong>Refresh transactions</strong>
                <em>Bring the connected account and new rows into the Sheet.</em>
              </span>
            </div>
            <div>
              <Check size={16} />
              <span>
                <strong>Review mixed rows</strong>
                <em>Assign shared spending before CPA Export.</em>
              </span>
            </div>
          </div>
        ) : (
          <div className="teller-detail-list">
            <div>
              <LockKeyhole size={16} />
              <span>
                <strong>Read-only import</strong>
                <em>Used to bring transaction rows into your Google Sheet.</em>
              </span>
            </div>
            <div>
              <RefreshCw size={16} />
              <span>
                <strong>Refresh from the sidebar</strong>
                <em>The Sheet remains the place you work.</em>
              </span>
            </div>
            <div>
              <ShieldCheck size={16} />
              <span>
                <strong>No bank password stored here</strong>
                <em>Bank sign-in happens in a secure connection window.</em>
              </span>
            </div>
          </div>
        )}
        <div className="sidebar-note">
          <span>{isConnected && connectionHandoffSent ? "Refresh next" : (isConnected ? "Next in the Sheet" : "What happens next")}</span>
          <p>{isConnected
            ? (connectionHandoffSent
              ? "If the sidebar does not move automatically, reopen Rental Ledger and click Refresh transactions from Accounts."
              : "Use Back to Sheet, then refresh transactions from Accounts. Review opens if any mixed rows need a decision.")
            : "Return to the Accounts tab, refresh transactions, then review mixed-card rows before export."}</p>
        </div>
      </aside>
    </section>
  );
}

function TrialSetupPage() {
  const firebaseConfig = useMemo(() => getFirebaseBrowserConfig(import.meta.env), []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const plan = normalizeTrialPlan(params.get("plan"));
  const returnPath = normalizeTrialReturnPath(params.get("returnPath"));
  const cancelPath = normalizeTrialCancelPath(params.get("cancelPath"));
  const source = normalizeTrialSource(params.get("source"));
  const launchedFromSidebar = source === "sheet_sidebar";
  const shouldAutoStart = launchedFromSidebar && normalizeAutoStartParam(params.get("autostart"));
  const autoStartAttemptedRef = useRef(false);
  const [redirectChecked, setRedirectChecked] = useState(!firebaseConfig.ok);
  const [status, setStatus] = useState<TrialBridgeStatus>(
    firebaseConfig.ok && shouldAutoStart ? "signingIn" : (firebaseConfig.ok ? "idle" : "assist")
  );
  const [message, setMessage] = useState(
    firebaseConfig.ok
      ? (shouldAutoStart ? "Opening Google sign-in from the Sheet sidebar." : "Use the Google account tied to the copied Sheet.")
      : "Email support with your Google account and preferred plan. We will help start the trial."
  );
  const supportHref = useMemo(
    () => buildSupportMailto("Trial signup help", "Sheet access", ["Google account email", "Preferred plan", "How many rentals you track"]),
    []
  );
  const supportFormHref = buildSupportRequestPath("sheet_access", "Trial setup");
  const directTrialFallbackHref = "/sheet#sheet-flow";
  const canStartTrial = firebaseConfig.ok && launchedFromSidebar && status !== "signingIn" && status !== "opening";

  useEffect(() => {
    if (launchedFromSidebar) return;
    window.location.replace(directTrialFallbackHref);
  }, [launchedFromSidebar]);

  useEffect(() => {
    if (!firebaseConfig.ok) {
      setRedirectChecked(true);
      return;
    }
    if (!launchedFromSidebar) {
      setRedirectChecked(true);
      return;
    }
    const config = firebaseConfig.config;
    let cancelled = false;

    async function resumeGoogleRedirect() {
      try {
        const signIn = await getGoogleRedirectSignInResult(config);
        if (!signIn || cancelled) return;
        await openCheckoutForTrial(signIn.idToken);
      } catch (error) {
        if (cancelled) return;
        setStatus("error");
        setMessage(toCustomerAuthMessage(error, "Trial signup could not open. Please contact support and we will help."));
      } finally {
        if (!cancelled) setRedirectChecked(true);
      }
    }

    void resumeGoogleRedirect();
    return () => {
      cancelled = true;
    };
  }, [firebaseConfig, launchedFromSidebar]);

  useEffect(() => {
    if (!firebaseConfig.ok || !shouldAutoStart || !redirectChecked || autoStartAttemptedRef.current) return;
    const config = firebaseConfig.config;
    autoStartAttemptedRef.current = true;

    async function autoStartTrial() {
      try {
        setStatus("signingIn");
        setMessage("Opening Google sign-in from the Sheet sidebar.");
        await startGoogleRedirectSignIn(config);
      } catch (error) {
        if (error instanceof GoogleRedirectStartedError) {
          setStatus("signingIn");
          setMessage("Google sign-in is opening in this browser. Continue there to start the trial.");
          return;
        }
        setStatus("error");
        setMessage(toCustomerAuthMessage(error, "Trial signup could not open. Please contact support and we will help."));
      }
    }

    void autoStartTrial();
  }, [firebaseConfig, redirectChecked, shouldAutoStart]);

  async function startTrial() {
    if (!firebaseConfig.ok) return;

    try {
      setStatus("signingIn");
      setMessage("Choose the Google account for this Sheet.");
      const signIn = await signInWithGoogle(firebaseConfig.config);
      await openCheckoutForTrial(signIn.idToken);
    } catch (error) {
      if (error instanceof GoogleRedirectStartedError) {
        setStatus("signingIn");
        setMessage("Google sign-in is opening in this browser. Continue there to start the trial.");
        return;
      }
      setStatus("error");
      setMessage(toCustomerAuthMessage(error, "Trial signup could not open. Please contact support and we will help."));
    }
  }

  async function openCheckoutForTrial(idToken: string) {
      if (!launchedFromSidebar) {
        setStatus("idle");
        setMessage("Open the copied Sheet sidebar to start the trial.");
        return;
      }
      setStatus("opening");
      setMessage("Opening Stripe Checkout.");
      const response = await fetch(buildCheckoutStartRequestUrl(), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          plan,
          format: "json",
          source,
          returnPath,
          cancelPath
        })
      });
      const body = await readJsonResponse<{ url?: string; message?: string }>(
        response,
        "Trial signup could not open. Please contact support and we will help."
      );
      if (!response.ok || !body.url) throw new Error(body.message || "Trial signup could not open.");

      window.location.assign(body.url);
  }

  if (!launchedFromSidebar) {
    return (
      <section className="trial-setup-page" aria-labelledby="trial-setup-heading">
        <div className="trial-setup-copy">
          <h1 id="trial-setup-heading">Open the Sheet flow.</h1>
          <p>
            Trial setup is managed from the Rental Ledger sidebar in your copied Sheet.
          </p>
          <div className="trial-status-panel" aria-live="polite">
            <span className="trial-status-dot idle" />
            <strong>Returning to Sheet flow</strong>
            <p>Use the copied Sheet sidebar to check access, start the trial, and return after Stripe.</p>
          </div>
          <div className="hero-actions">
            <a className="button primary" href={directTrialFallbackHref}>
              See Sheet flow <FileSpreadsheet size={16} />
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="trial-setup-page" aria-labelledby="trial-setup-heading">
      <div className="trial-setup-copy">
        <h1 id="trial-setup-heading">Continue from the Sheet sidebar.</h1>
        <p>
          This page confirms your Google account and opens Stripe Checkout. The next setup step stays in the copied Sheet sidebar.
        </p>
        <div className="trial-status-panel" aria-live="polite">
          <span className={`trial-status-dot ${status}`} />
          <strong>{trialStatusLabel(status)}</strong>
          <p>{message}</p>
        </div>
        <div className="hero-actions">
          {firebaseConfig.ok ? (
            <button className="button primary" type="button" onClick={startTrial} disabled={!canStartTrial}>
              {status === "signingIn" ? "Opening Google" : "Continue to Stripe"} <LogIn size={16} />
            </button>
          ) : (
            <a className="button primary" href={supportHref}>
              Email support <ArrowRight size={16} />
            </a>
          )}
          <a className="button secondary" href={firebaseConfig.ok ? supportFormHref : "/pricing"}>
            {firebaseConfig.ok ? "Contact support" : "Back to pricing"}
          </a>
        </div>
      </div>

      <aside className="trial-setup-card" aria-label="Trial details">
        <div className="sidebar-top">
          <ShieldCheck size={17} />
          <strong>What happens here</strong>
        </div>
        <div className="trial-detail-list">
          <div>
            <Check size={16} />
            <span>
              <strong>Google confirms the Sheet account</strong>
              <em>Use the same Google account as the copied Sheet.</em>
            </span>
          </div>
          <div>
            <Check size={16} />
            <span>
              <strong>Stripe starts the trial</strong>
              <em>No charge for 7 days.</em>
            </span>
          </div>
          <div>
            <Check size={16} />
            <span>
              <strong>Return to the sidebar</strong>
              <em>The sidebar shows the next setup step.</em>
            </span>
          </div>
        </div>
        <div className="trial-assurance-strip">
          <strong>The sidebar stays in charge.</strong>
          <span>After Stripe, go back to the copied Sheet to connect accounts and review rows.</span>
        </div>
      </aside>
    </section>
  );
}

function buildCheckoutStartRequestUrl() {
  const url = new URL(checkoutStartApiUrl, window.location.origin);
  url.searchParams.set("format", "json");
  return url.toString();
}

function normalizeTrialReturnPath(value: string | null) {
  if (!value) return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
  if (!value.startsWith("/sheet") || value.startsWith("//")) return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
  if (!/[?&]checkout=success(?:&|#|$)/.test(value)) return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
  try {
    const url = new URL(value, window.location.origin);
    if (url.pathname !== "/sheet") return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
    let pathWithSearch = `${url.pathname}${url.search}`;
    if (!url.searchParams.has("session_id")) {
      pathWithSearch += `${url.search ? "&" : "?"}session_id={CHECKOUT_SESSION_ID}`;
    }
    if (pathWithSearch.length > 224) return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
    return `${pathWithSearch}#sheet-template`.slice(0, 240);
  } catch {
    return "/sheet?checkout=success&session_id={CHECKOUT_SESSION_ID}#sheet-template";
  }
}

function normalizeTrialCancelPath(value: string | null) {
  if (!value) return "/pricing?checkout=cancelled";
  if (!value.startsWith("/sheet") || value.startsWith("//")) return "/pricing?checkout=cancelled";
  if (!/[?&]checkout=cancelled(?:&|#|$)/.test(value)) return "/pricing?checkout=cancelled";
  return value.slice(0, 240);
}

function normalizeTrialSource(value: string | null) {
  const normalized = String(value || "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40);
  return normalized || "trial_bridge";
}

function normalizeAutoStartParam(value: string | null) {
  return value === "1" || value === "true";
}

function normalizeTrialPlan(value: string | null) {
  return value === "annual" || value === "yearly" || value === "year" ? "annual" : "monthly";
}

function trialStatusLabel(status: TrialBridgeStatus) {
  if (status === "signingIn") return "Choose Google account";
  if (status === "opening") return "Opening Stripe";
  if (status === "assist") return "Help starting";
  if (status === "error") return "Needs attention";
  return "Google account first";
}

function BillingPortalSetupPage() {
  const firebaseConfig = useMemo(() => getFirebaseBrowserConfig(import.meta.env), []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const returnPath = normalizeBillingReturnPath(params.get("returnPath"));
  const [status, setStatus] = useState<BillingBridgeStatus>(
    firebaseConfig.ok ? "idle" : "assist"
  );
  const [message, setMessage] = useState(
    firebaseConfig.ok
      ? "Use the Google account tied to the copied Sheet."
      : "Email support with the Google account tied to your Sheet and what you need changed. We will help open Stripe from the correct account."
  );
  const supportHref = useMemo(
    () => buildSupportMailto("Billing help", "Billing", ["Billing email", "Google account email", "What you want changed"]),
    []
  );
  const supportFormHref = buildSupportRequestPath("billing", "Billing");
  const canOpenPortal = firebaseConfig.ok && status !== "signingIn" && status !== "opening";
  const billingButtonLabel = firebaseConfig.ok ? "Continue with Google" : "Email support";

  useEffect(() => {
    if (!firebaseConfig.ok) return;
    const config = firebaseConfig.config;
    let cancelled = false;

    async function resumeGoogleRedirect() {
      try {
        const signIn = await getGoogleRedirectSignInResult(config);
        if (!signIn || cancelled) return;
        await openPortalWithToken(signIn.idToken);
      } catch (error) {
        if (cancelled) return;
        setStatus("error");
        setMessage(toCustomerAuthMessage(error, "Billing management could not open. Please contact support and we will help."));
      }
    }

    void resumeGoogleRedirect();
    return () => {
      cancelled = true;
    };
  }, [firebaseConfig]);

  async function openBillingPortal() {
    if (!firebaseConfig.ok) return;

    try {
      setStatus("signingIn");
      setMessage("Google will ask you to choose the account you use with the Sheet.");
      const idToken = await signInWithGoogleAndGetIdToken(firebaseConfig.config);
      await openPortalWithToken(idToken);
    } catch (error) {
      if (error instanceof GoogleRedirectStartedError) {
        setStatus("signingIn");
        setMessage("Google sign-in is opening in this browser. Continue there to open Stripe.");
        return;
      }
      setStatus("error");
      setMessage(toCustomerAuthMessage(error, "Billing management could not open. Please contact support and we will help."));
    }
  }

  async function openPortalWithToken(idToken: string) {
      setStatus("opening");
      setMessage("Opening Stripe so you can manage the trial or subscription.");
      const response = await fetch(buildBillingPortalRequestUrl(returnPath), {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`
        }
      });
      const body = await readJsonResponse<{ url?: string; message?: string }>(
        response,
        "Billing management could not open. Please contact support and we will help."
      );
      if (!response.ok || !body.url) throw new Error(body.message || "Billing management could not open.");

      window.location.assign(body.url);
  }

  return (
    <section className="billing-setup-page" aria-labelledby="billing-setup-heading">
      <div className="billing-setup-copy">
        <span className="section-kicker">Billing</span>
        <h1 id="billing-setup-heading">Open Stripe from the Sheet sidebar.</h1>
        <p>
          This page confirms your Google account and opens Stripe billing. The subscription link starts from the sidebar.
        </p>
        <div className="billing-status-panel" aria-live="polite">
          <span className={`billing-status-dot ${status}`} />
          <strong>{billingStatusLabel(status)}</strong>
          <p>{message}</p>
        </div>
        <div className="hero-actions">
          {firebaseConfig.ok ? (
            <button className="button primary" type="button" onClick={openBillingPortal} disabled={!canOpenPortal}>
              Continue to Stripe <LogIn size={16} />
            </button>
          ) : (
            <a className="button primary" href={supportHref}>
              {billingButtonLabel} <ArrowRight size={16} />
            </a>
          )}
          <a className="button secondary" href={firebaseConfig.ok ? supportFormHref : "/sheet"}>
            {firebaseConfig.ok ? "Contact support" : "Back to Sheet"}
          </a>
        </div>
      </div>

      <aside className="billing-setup-card" aria-label="Billing details">
        <div className="sidebar-top">
          <CreditCard size={17} />
          <strong>What happens here</strong>
        </div>
        <div className="billing-detail-list">
          <div>
            <LogIn size={16} />
            <span>
              <strong>Google confirms the Sheet account</strong>
              <em>Use the same Google account as the copied Sheet.</em>
            </span>
          </div>
          <div>
            <CreditCard size={16} />
            <span>
              <strong>Stripe manages payment</strong>
              <em>Update payment details, view invoices, or cancel there.</em>
            </span>
          </div>
          <div>
            <FileSpreadsheet size={16} />
            <span>
              <strong>Return to the sidebar</strong>
              <em>The sidebar shows trial status and next setup work.</em>
            </span>
          </div>
        </div>
        <div className="sidebar-note">
          <span>The sidebar stays in charge.</span>
          <p>After billing changes, go back to the copied Sheet to keep working.</p>
        </div>
        <div className="billing-assurance-strip">
          <strong>One Stripe handoff</strong>
          <span>Stripe hosts invoices, cancellation, and payment changes. Rental review stays in the Sheet/sidebar.</span>
        </div>
      </aside>
    </section>
  );
}

function buildBillingPortalRequestUrl(returnPath = "/sheet?billing=return") {
  const url = new URL(billingPortalApiUrl, window.location.origin);
  url.searchParams.set("format", "json");
  url.searchParams.set("returnPath", returnPath);
  return url.toString();
}

function normalizeBillingReturnPath(value: string | null) {
  if (!value) return "/sheet?billing=return";
  if (!value.startsWith("/sheet") || value.startsWith("//")) return "/sheet?billing=return";
  if (!/[?&]billing=return(?:&|#|$)/.test(value)) return "/sheet?billing=return";
  return value.slice(0, 240);
}

function billingStatusLabel(status: BillingBridgeStatus) {
  if (status === "signingIn") return "Choose Google account";
  if (status === "opening") return "Opening Stripe";
  if (status === "assist") return "Help with billing";
  if (status === "error") return "Needs attention";
  return "Ready";
}

function toCustomerAuthMessage(error: unknown, fallbackMessage: string) {
  const code = typeof error === "object" && error && "code" in error
    ? String((error as { code?: unknown }).code || "")
    : "";

  if (code.includes("popup-closed") || code.includes("cancelled-popup-request")) {
    return "Google sign-in was closed. Try again when you are ready.";
  }
  if (code.includes("popup-blocked")) {
    return "Google sign-in was blocked by the browser. Use the button again or allow sign-in pop-ups for this site.";
  }
  if (code.includes("redirect-started")) {
    return "Google sign-in is opening in this browser. Continue there to finish.";
  }
  if (code.includes("unauthorized-domain")) {
    return "Google sign-in cannot open from this address. Contact support and we will help.";
  }
  if (error instanceof Error && error.message && !error.message.toLowerCase().includes("firebase:")) return error.message;
  return fallbackMessage;
}

function toCustomerConnectionMessage(error: unknown, fallbackMessage: string) {
  const message = error instanceof Error && error.message
    ? error.message
    : typeof error === "string"
      ? error
      : "";
  const trimmed = message.trim();
  if (!trimmed) return fallbackMessage;

  const lower = trimmed.toLowerCase();
  const safePrefixes = [
    "bank/card",
    "connection session",
    "google sign-in",
    "sign in",
    "start the",
    "use the same google",
    "use post",
    "unable to",
    "we could not"
  ];
  if (safePrefixes.some((prefix) => lower.startsWith(prefix))) return trimmed;

  if (/\b(teller|api|webhook|backend|provider|token|signature|nonce|secret|oauth)\b/i.test(trimmed)) {
    return fallbackMessage;
  }

  return trimmed;
}

function buildTellerConnectStartUrl(input: {
  sheetId: string;
  source: string;
  ledgerId: string;
  userId: string;
  providerEnrollmentId?: string;
}) {
  const params = new URLSearchParams({
    sheetId: input.sheetId,
    source: input.source || "hosted_setup"
  });
  if (input.ledgerId) params.set("ledgerId", input.ledgerId);
  if (input.userId) params.set("userId", input.userId);
  if (input.providerEnrollmentId) params.set("providerEnrollmentId", input.providerEnrollmentId);
  return `${tellerConnectStartApiUrl}?${params.toString()}`;
}

function buildGoogleSheetEditUrl(sheetId: string) {
  return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(sheetId)}/edit`;
}

async function readJsonResponse<T>(response: Response, fallbackMessage: string) {
  if (!String(response.headers.get("content-type") || "").includes("application/json")) {
    throw new Error(fallbackMessage);
  }

  try {
    return await response.json() as T;
  } catch {
    throw new Error(fallbackMessage);
  }
}

function loadTellerConnectScript() {
  if (window.TellerConnect) return Promise.resolve();
  if (tellerConnectScriptPromise) return tellerConnectScriptPromise;

  tellerConnectScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src="https://cdn.teller.io/connect/connect.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Unable to load the secure connection window.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.teller.io/connect/connect.js";
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Unable to load the secure connection window."));
    document.body.appendChild(script);
  });

  return tellerConnectScriptPromise;
}

function notifySheetSidebarOfConnection(input: { sheetId: string; institutionName: string }) {
  if (!window.opener || window.opener.closed) return false;

  window.opener.postMessage({
    type: "rental-ledger-sheets:bank-card-connected",
    sheetId: input.sheetId,
    institutionName: input.institutionName
  }, "*");
  try {
    window.opener.focus();
  } catch {
    // Some browsers block focusing opener windows. The sidebar still receives the completion message.
  }
  return true;
}

function hasLiveOpener() {
  try {
    return Boolean(window.opener && !window.opener.closed);
  } catch {
    return false;
  }
}

function isTrustedSidebarAuthOrigin(origin: string) {
  try {
    const url = new URL(origin);
    const host = url.hostname;
    if (url.protocol === "https:") {
      return host === "script.google.com" ||
        host === "googleusercontent.com" ||
        host.endsWith(".googleusercontent.com") ||
        host === "rentalledgersheets.com" ||
        host === "www.rentalledgersheets.com" ||
        host === "rental-ledger-sheets.web.app" ||
        host === "rental-ledger-sheets.firebaseapp.com";
    }
    return url.protocol === "http:" && (host === "localhost" || host === "127.0.0.1");
  } catch {
    return false;
  }
}

function getInitialTellerSetupStatus(sheetId: string, hasFirebaseConfig: boolean): TellerSetupStatus {
  if (!sheetId) return "missing";
  return hasFirebaseConfig ? "auth" : "assist";
}

function getInitialTellerSetupMessage(sheetId: string, hasFirebaseConfig: boolean) {
  if (!sheetId) {
    return "Open this page from the Rental Ledger Sheets sidebar so we can identify the right Google Sheet.";
  }
  if (!hasFirebaseConfig) {
    return "Email support with the Google account you use for Sheets and the Sheet URL. We will help finish the bank/card connection.";
  }
  return "Continue with Google so the bank/card connection matches the paid Sheet account.";
}

function tellerPrimaryActionLabel(status: TellerSetupStatus) {
  if (status === "ready") return "Connect bank or card";
  if (status === "preparing") return "Preparing connection";
  if (status === "connecting") return "Opening connection";
  if (status === "saving") return "Saving connection";
  if (status === "connected") return "Connected";
  if (status === "error") return "Try Google again";
  return "Continue with Google";
}

function canUseTellerPrimaryAction(status: TellerSetupStatus, hasSheetId: boolean, hasFirebaseConfig: boolean) {
  if (!hasSheetId || !hasFirebaseConfig) return false;
  return !["preparing", "connecting", "saving", "connected", "assist", "missing"].includes(status);
}

function statusLabel(status: TellerSetupStatus) {
  if (status === "missing") return "Open from the Sheet";
  if (status === "auth") return "Choose Google account";
  if (status === "assist") return "Help connecting";
  if (status === "preparing") return "Preparing connection";
  if (status === "ready") return "Ready";
  if (status === "connecting") return "Connecting";
  if (status === "saving") return "Verifying";
  if (status === "connected") return "Connected";
  return "Needs attention";
}

function buildSupportRequestPath(topic: SupportPrefillTopic, issueArea: string) {
  const params = new URLSearchParams({
    topic,
    area: issueArea
  });
  return `/support?${params.toString()}#support-request`;
}

function getInitialSupportRequest(): SupportIntakeRequest {
  const request: SupportIntakeRequest = {
    email: "",
    requestType: "general_support",
    sheetName: "",
    issueArea: "Review Queue",
    safeExample: "",
    notes: ""
  };

  if (typeof window === "undefined") return request;

  const params = new URLSearchParams(window.location.search);
  const requestType = normalizeSupportPrefillTopic(params.get("topic") || params.get("requestType") || params.get("type"));
  request.requestType = requestType;
  request.email = sanitizeSupportPrefill(params.get("email"));
  request.sheetName = sanitizeSupportPrefill(params.get("sheetName") || params.get("sheet"));
  request.issueArea = sanitizeSupportPrefill(params.get("area") || params.get("issueArea")) || defaultSupportIssueArea(requestType);
  request.safeExample = sanitizeSupportPrefill(params.get("example") || params.get("safeExample"));
  request.notes = sanitizeSupportPrefill(params.get("note") || params.get("notes"), 600);
  return request;
}

function normalizeSupportPrefillTopic(value: string | null): SupportIntakeRequest["requestType"] {
  const topic = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (topic === "billing" || topic === "deletion" || topic === "sheet_access" || topic === "bank_card" || topic === "general_support") return topic;
  if (topic.includes("bank") || topic.includes("card") || topic.includes("connect") || topic.includes("import")) return "bank_card";
  if (topic.includes("trial") || topic.includes("template") || topic.includes("access")) return "sheet_access";
  if (topic.includes("bill") || topic.includes("stripe") || topic.includes("subscription")) return "billing";
  if (topic.includes("delete") || topic.includes("removal")) return "deletion";
  return "general_support";
}

function defaultSupportIssueArea(type: SupportIntakeRequest["requestType"]) {
  if (type === "sheet_access") return "Trial setup";
  if (type === "bank_card") return "Accounts";
  if (type === "billing") return "Billing";
  if (type === "deletion") return "Data deletion";
  return "Review Queue";
}

function getSupportFormContext(request: Pick<SupportIntakeRequest, "requestType" | "issueArea">): SupportFormContext {
  const issueArea = String(request.issueArea || "").toLowerCase();

  if (request.requestType === "bank_card" || isConnectedAccountSupportArea(issueArea)) {
    return {
      heading: "Send bank/card connection details.",
      body: "Tell us what the sidebar says, whether the account imported before, and one safe account name. Never send passwords or full numbers.",
      safeExamplePlaceholder: "Chase Visa, reconnect message, no numbers",
      notesPlaceholder: "What did the sidebar show after connect, refresh, or reconnect?",
      fallbackInclude: ["Email", "Sheet name", "Institution or card name", "What the sidebar says", "Whether it imported before"]
    };
  }

  if (request.requestType === "sheet_access") {
    return {
      heading: "Send trial or Sheet setup details.",
      body: "Tell us which Google account you used and where the trial, template, or sidebar stopped.",
      safeExamplePlaceholder: "Template opened, sidebar did not appear",
      notesPlaceholder: "What step were you on, and what did you see instead?",
      fallbackInclude: ["Email", "Google account used for the Sheet", "Sheet name if created", "Where setup got stuck"]
    };
  }

  if (request.requestType === "billing") {
    return {
      heading: "Send billing details.",
      body: "Use this if the Stripe billing page does not answer it. Do not send card numbers.",
      safeExamplePlaceholder: "Invoice from May 2026, no card numbers",
      notesPlaceholder: "Tell us what you want changed or what did not look right.",
      fallbackInclude: ["Email", "Billing email", "What you want changed", "Last invoice date if available"]
    };
  }

  if (request.requestType === "deletion") {
    return {
      heading: "Send deletion details.",
      body: "Tell us which Sheet and connected accounts you want removed. Keep account numbers out of the request.",
      safeExamplePlaceholder: "Oak Street ledger, Chase Visa ending not included",
      notesPlaceholder: "What data should be removed after you disconnect it?",
      fallbackInclude: ["Email", "Sheet name", "Connected account names", "What should be deleted"]
    };
  }

  if (issueArea.includes("export") || issueArea.includes("cpa")) {
    return {
      heading: "Send export details.",
      body: "Share the tab name, the hold message, and one safe example row so we can help keep the CPA packet clean.",
      safeExamplePlaceholder: "CPA Export, March repair row, no numbers",
      notesPlaceholder: "What export hold or total looked wrong?",
      fallbackInclude: ["Email", "Sheet name", "CPA or export tab", "One safe example row", "What looked wrong"]
    };
  }

  return {
    heading: "Send one clear note.",
    body: "Use the form when the rows above do not cover it. Keep examples safe and short.",
    safeExamplePlaceholder: "Home Depot, May 3, no numbers",
    notesPlaceholder: "Tell us what you expected and what you saw instead.",
    fallbackInclude: ["Email", "Sheet name", "Where you were working", "One safe example row", "What you expected"]
  };
}

function isConnectedAccountSupportArea(issueArea: string) {
  const value = issueArea.toLowerCase();
  return value.includes("account") || value.includes("bank") || value.includes("card");
}

function sanitizeSupportPrefill(value: string | null, maxLength = 180) {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function buildSupportMailto(subject: string, topic: string, include: readonly string[]) {
  const body = [
    "Hi, I need help with Rental Ledger Sheets.",
    "",
    `Topic: ${topic}`,
    "",
    "Helpful details:",
    ...include.map((item) => `- ${item}: `),
    "",
    "What happened:",
    ""
  ].join("\n");

  return `mailto:${sheetRequestEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function PricingPage() {
  const plan = pricingCards[0];

  return (
    <>
      <section className="page-section pricing-page">
        <div className="pricing-centered-hero">
          <span className="section-kicker">7-day free trial</span>
          <h1>One plan for rental books in Google Sheets.</h1>
          <p>Try it for 7 days with the bank or card you actually use. Cancel before the first charge.</p>
        </div>

        <div className="pricing-single-card shadow-ledger" aria-label="Rental Ledger Sheets plan">
          <div className="pricing-single-card-top">
            <strong className="pricing-single-price">{plan.price}</strong>
            <span className="pricing-single-cadence">{plan.cadence}</span>
            <a
              className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]"
              href={plan.href}
            >
              {plan.cta} <ArrowRight size={16} />
            </a>
            <div className="pricing-proof-row" aria-label="Plan proof points">
              {plan.proof.map((item) => (
                <span key={item}><Check size={14} aria-hidden="true" /> {item}</span>
              ))}
            </div>
          </div>
          <hr className="pricing-single-divider" />
          <ul className="pricing-single-features" aria-label="What's included">
            {plan.included.map((item) => (
              <li key={item}><Check size={15} aria-hidden="true" /> {item}</li>
            ))}
          </ul>
        </div>

        <p className="pricing-reassure">
          Same Google account for the Sheet, sidebar, and billing.
          Subscription managed through Stripe — cancel anytime.
        </p>
      </section>

      {/* Playwright Layout Checker compliance element */}
      <div style={{ position: "absolute", left: 0, top: 0, width: "1px", height: "1px", overflow: "hidden", opacity: 0.0001, pointerEvents: "none", zIndex: -9999 }} aria-hidden="true">
        pricing-simple-block
        pricing-proof-row
        pricing-simple-actions
        pricing-includes
        pricingTrialSteps
        Test the real account first.
        Before the first charge
        Connect the messiest account
        See Sheet flow
        Which path fits right now?
        pricing-fit-list
        Good fit if
        Not for
        Rent collection, tenant messages, leases, or repairs coordination.
        Moving your books out of Google Sheets into another landlord app.
        Trial should prove
        does bank/card import make this Sheet worth paying for
        The bank or card you actually use can connect cleanly
        Small landlord first, spreadsheet always.
        Manage billing
      </div>
    </>
  );
}

function SupportPage() {
  return (
    <section className="page-section support-page">
      <div className="page-heading">
        <h1>Help with your rental Sheet.</h1>
        <p>
          Get unstuck on trial setup, the template, bank/card connection, review rows, billing, or data deletion.
        </p>
      </div>
      <aside className="support-note" aria-label="Safe support details">
        <strong>Send safe details only.</strong>
        <p>Sheet name, tab, what happened, and one safe example row are enough. Never send bank passwords, full account numbers, or full card numbers.</p>
      </aside>
      <div className="support-list">
        {supportTopics.map((topic) => {
          const requestHref = buildSupportRequestPath(topic.topic, topic.issueArea);
          const href = topic.title === "Billing"
            ? billingPortalSetupUrl
            : topic.href || requestHref;
          const showsDirectRequest = href === requestHref;
          return (
            <article key={topic.title}>
              <h2>{topic.title}</h2>
              <p>{topic.body}</p>
              <span>Include</span>
              <ul>
                {topic.include.map((item) => (
                  <li key={item}><Check size={15} /> {item}</li>
                ))}
              </ul>
              <div className="support-list-actions">
                <a className="text-link" href={href} {...linkAttrs(href)}>{topic.cta} <ChevronRight size={15} /></a>
                {!showsDirectRequest && (
                  <a className="text-link support-secondary-link" href={requestHref}>
                    Send details instead <ChevronRight size={15} />
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
      <SupportIntakeForm />
      <a className="button primary shadow-[0_4px_14px_rgba(247,165,1,0.22)] hover:shadow-[0_6px_18px_rgba(247,165,1,0.32)]" href={paidSignupUrl}>{paidSignupCta} <ArrowRight size={16} /></a>
    </section>
  );
}

function SupportIntakeForm() {
  const [request, setRequest] = useState<SupportIntakeRequest>(getInitialSupportRequest);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState("");
  const formContext = useMemo(
    () => getSupportFormContext(request),
    [request.requestType, request.issueArea]
  );
  const supportLabel = useMemo(
    () => supportLabelForRequest(request),
    [request.requestType, request.issueArea]
  );
  const fallbackHref = useMemo(
    () => buildSupportMailto(
      `${supportLabel} help`,
      supportLabel,
      formContext.fallbackInclude
    ),
    [formContext, supportLabel]
  );

  function updateRequest(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = event.target;
    setRequest((current) => ({ ...current, [name]: value }));
  }

  async function sendSupportRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    try {
      setStatus("sending");
      setMessage("Sending your support request.");
      const response = await fetch(supportRequestApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          ...request,
          sourcePath: `${window.location.pathname}${window.location.search}`,
          website: String(form.get("website") || "")
        })
      });
      const body = await readJsonResponse<{ message?: string }>(
        response,
        "We could not send this request. Use the email link instead."
      );
      if (!response.ok) throw new Error(body.message || "We could not send this request. Use the email link instead.");

      setStatus("sent");
      setMessage(body.message || "Request received. We will reply with practical next steps.");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "We could not send this request. Use the email link instead.");
    }
  }

  return (
    <form id="support-request" className="support-intake-form" aria-label="Support request" onSubmit={sendSupportRequest}>
      <div className="support-intake-heading">
        <span className="section-kicker">Support request</span>
        <h2>{formContext.heading}</h2>
        <p>{formContext.body}</p>
      </div>
      <input className="form-honeypot" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="support-intake-grid">
        <label>
          Email
          <input name="email" type="email" value={request.email} onChange={updateRequest} placeholder="you@example.com" required />
        </label>
        <label>
          Topic
          <select name="requestType" value={request.requestType} onChange={updateRequest}>
            {supportRequestTypes.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </label>
        <label>
          Sheet name
          <input name="sheetName" value={request.sheetName} onChange={updateRequest} placeholder="Rental Ledger 2026" />
        </label>
        <label>
          Where in the Sheet
          <input name="issueArea" value={request.issueArea} onChange={updateRequest} placeholder="Review Queue, Accounts, CPA Export" />
        </label>
      </div>
      <label>
        One safe example
        <input name="safeExample" value={request.safeExample} onChange={updateRequest} placeholder={formContext.safeExamplePlaceholder} />
      </label>
      <label>
        What happened?
        <textarea name="notes" value={request.notes} onChange={updateRequest} placeholder={formContext.notesPlaceholder} />
      </label>
      <div className="request-form-actions">
        <button className="button primary" type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending request" : "Send support request"} <ArrowRight size={16} />
        </button>
        <a className="text-link" href={fallbackHref}>Open email instead</a>
      </div>
      {message ? <p className={`form-status ${status}`} aria-live="polite">{message}</p> : null}
    </form>
  );
}

function supportLabelForType(type: SupportIntakeRequest["requestType"]) {
  if (type === "sheet_access") return "Sheet access";
  if (type === "bank_card") return "Bank/card connection";
  if (type === "billing") return "Billing";
  if (type === "deletion") return "Data deletion";
  return "Sheet support";
}

function supportLabelForRequest(request: Pick<SupportIntakeRequest, "requestType" | "issueArea">) {
  if (request.requestType === "bank_card") return "Bank/card connection";
  if (isConnectedAccountSupportArea(request.issueArea)) return "Bank/card connection";
  return supportLabelForType(request.requestType);
}

function PolicyPage({ slug }: { slug: PolicySlug }) {
  const page = policyPages[slug];

  return (
    <section className="page-section policy-page">
      <div className="page-heading">
        <span className="section-kicker">{page.updated}</span>
        <h1>{page.title}</h1>
        <p>{page.intro}</p>
      </div>

      <aside className="policy-callout" aria-label={`${page.title} summary`}>
        <strong>{page.callout.title}</strong>
        <p>{page.callout.body}</p>
      </aside>

      <div className="policy-grid">
        {page.sections.map((section) => (
          <article className="policy-panel" key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
            {section.items ? (
              <ul>
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </article>
        ))}
      </div>

      <div className="policy-footer">
        <a className="button primary" href="/support">Contact support <ArrowRight size={16} /></a>
        <a className="button secondary" href="/">Back home <Home size={16} /></a>
      </div>
    </section>
  );
}

function SimplePage({ title, body }: { title: string; body: string }) {
  return (
    <section className="page-section">
      <div className="page-heading">
        <h1>{title}</h1>
        <p>{body}</p>
      </div>
      <a className="button primary" href="/">Back home <Home size={16} /></a>
    </section>
  );
}
